import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClearBacklog } from '../../components/portal/ClearBacklog';
import { DispatchSearch } from '../../components/portal/DispatchSearch';
import { DispatchGroup } from '../../components/portal/DispatchRows';
import { currentStaffEmail, daysSince, fetchConversations, markConversationDone, reopenConversation, type Conversation } from '../../utils/conversations';

/**
 * DISPATCHES — who owes whom a reply (25 Sep 2026).
 *
 * Named "Conversations" for about an hour. Tom: "conversations is odd." So
 * were Waiting, Follow-ups, Threads, Chasing, Open Threads, The Desk,
 * Messages, Letters, Mailbag, Ongoing and Traffic. It came down to Exchange
 * and Dispatches, and **Tom chose Dispatches**.
 *
 * Recorded because the argument against it is real and somebody will make it
 * again: a dispatch is something you SEND, and half this screen is mail coming
 * the other way that Portal has not answered. Exchange was true in both
 * directions. Tom preferred the word anyway, which is his call — it is his
 * studio's vocabulary, not the schema's. **Do not quietly rename it back.**
 *
 * The FILES, TABLES AND RPCS are all still called `conversation*`. That is
 * also deliberate: renaming three tables and seven functions to match a label
 * costs a migration and buys nothing. The word on screen is Dispatches; the
 * word in the code is conversations. Do not half-rename it either.
 *
 * TWO GROUPS. THAT IS THE WHOLE SCREEN.
 *
 *   Waiting on Portal — they spoke last.
 *   Waiting on them   — Portal spoke last.
 *
 * There used to be a third at the top, called Broken, in a highlighted panel:
 * bounces, auto-reply-only threads, and anything chased three times or more.
 * Tom killed it — *"The broken one is the most prominent but I don't
 * understand the point of this"*, then *"I don't think we even need the broken
 * bit"*. He was right. It bundled three unlike things, and with no bounces
 * open every row in the loudest box on the page was a plain chase, repeating
 * what the Chasing list already said.
 *
 * **The facts survive as a line on the row; only the category went** — see
 * `rowNote`. A bounce still says so, because that is the one thing nothing
 * else in Portal OS or Gmail will tell you, and a bounced thread sorts into
 * Waiting on Portal by itself, without being told to: the last thing that
 * happened was not Portal speaking, so Portal has to send it again.
 *
 * The general lesson, since it keeps recurring here: **a fact can be shown
 * without being given a category.** A category costs every reader every day.
 *
 * THE PANEL IS UNIVERSAL. The grey card treatment was the Broken group's alarm
 * styling; Tom liked the look and asked for it on everything. Applied to every
 * row it cannot signal anything, which is the point — it is decoration now,
 * not a warning.
 *
 * THERE IS NO STANDFIRST UNDER THE TITLE. There was one sentence explaining
 * what the screen showed. Tom: *"remove this — it's needless."* He is right and
 * the test is a good one to keep: a line of prose describing a screen is an
 * admission the screen does not explain itself. Two headed groups, a name, a
 * subject and a number of days do explain themselves. **Do not reintroduce
 * a description here.**
 *
 * Sorting is OLDEST FIRST inside each group, because this screen exists for
 * what has fallen out of view; today's mail is already in the inbox.
 *
 * ONE BUTTON PER ROW, AND IT SAYS DONE. There was briefly a second, "Not a
 * conversation", which blacklisted the sender's domain for good. Tom cut it on
 * sight: *"This is just making everything convoluted — which is the ONE thing
 * I want to avoid all the time (and worth remembering all the time as we build
 * and tweak this OS)."* Same reason there is no filter and no keyboard
 * shortcut. The box at the top asks a question; that is all it does.
 *
 * **THE ROWS LIVE IN components/portal/DispatchRows.tsx (16 Oct 2026)**,
 * which is also where the day count stopped being the loudest thing on each
 * one. The reasoning is written out there.
 */

type View = 'open' | 'needs' | 'chase' | 'done';
const TABS: {
  view: View;
  label: string;
}[] = [{
  view: 'open',
  label: 'Open'
}, {
  view: 'needs',
  label: 'Needs you'
}, {
  view: 'chase',
  label: 'Chasing'
}, {
  view: 'done',
  label: 'Done'
}];
export function Conversations() {
  const [rows, setRows] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('open');
  const [oldestFirst, setOldestFirst] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [undo, setUndo] = useState<Conversation | null>(null);

  /**
   * Who is reading. Every Gmail link is built for this address — see
   * `gmailLink`. Without it Zeena's clicks land in Tom's account.
   */
  const [viewer, setViewer] = useState('');
  const load = useCallback(async () => {
    setRows(await fetchConversations());
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
    void currentStaffEmail().then(setViewer);
  }, [load]);

  /**
   * Reload when the window comes back to the front.
   *
   * Done is shared — one row per thread, not one per person — so when Zeena
   * marks something it is true for Tom immediately in the database and not at
   * all on his screen, which had already loaded. Somebody looking at a list
   * their colleague cleared ten minutes ago is worse than no list. This is the
   * cheap half of the fix; a live subscription is the expensive half and is
   * not worth it for two people in one studio.
   */
  useEffect(() => {
    function refresh() {
      if (document.visibilityState === 'visible') void load();
    }
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [load]);

  /* The toast clears itself; a mark you can only undo by remembering to is not
     really undoable. */
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(null), 7000);
    return () => window.clearTimeout(timer);
  }, [undo]);
  const groups = useMemo(() => {
    const open = rows.filter(row => !row.done);
    const done = rows.filter(row => row.done);
    const byAge = (a: Conversation, b: Conversation) => {
      const diff = daysSince(b.lastAt) - daysSince(a.lastAt);
      return oldestFirst ? diff : -diff;
    };
    return {
      needs: open.filter(row => !row.lastFromPortal).sort(byAge),
      chase: open.filter(row => row.lastFromPortal).sort(byAge),
      done: done.sort(byAge),
      openCount: open.length
    };
  }, [rows, oldestFirst]);
  async function onDone(row: Conversation) {
    setBusyId(row.threadId);
    const ok = await markConversationDone(row.threadId, row.lastAt);
    setBusyId('');
    if (!ok) return;
    setRows(current => current.map(item => item.threadId === row.threadId ? {
      ...item,
      done: true
    } : item));
    setUndo(row);
  }
  async function onReopen(row: Conversation) {
    setBusyId(row.threadId);
    const ok = await reopenConversation(row.threadId);
    setBusyId('');
    if (!ok) return;
    setRows(current => current.map(item => item.threadId === row.threadId ? {
      ...item,
      done: false
    } : item));
    setUndo(null);
  }
  const counts = {
    open: groups.openCount,
    needs: groups.needs.length,
    chase: groups.chase.length,
    done: groups.done.length
  };
  const showNeeds = view === 'open' || view === 'needs';
  const showChase = view === 'open' || view === 'chase';
  const showDone = view === 'done';
  const nothing = !loading && (showDone && groups.done.length === 0 || !showDone && (showNeeds ? groups.needs.length : 0) + (showChase ? groups.chase.length : 0) === 0);
  return <div data-id="element-2372" data-mp="butmz1s4n4i">
      <h1 className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2373" data-mp="c4iukf02q95">Dispatches</h1>

      <DispatchSearch data-id="element-2374" data-mp="35c52tsqmrcco" />

      {/* The filter row doubles as the summary — the counts are the only
          numbers on the page worth showing at a glance. */}
      <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 border-b border-ruleDark pb-1" data-id="element-2375" data-mp="2vmbr8rqhmx8f">
        {TABS.map(tab => {
        const active = view === tab.view;
        return <button key={tab.view} type="button" onClick={() => setView(tab.view)} aria-current={active ? 'page' : undefined} className={`label -mb-[1px] inline-flex h-11 items-center gap-2.5 border-b transition-colors duration-150 ease-out ${active ? 'border-paper text-paper' : 'border-transparent text-paper/45 hover:text-paper'}`} data-id="element-2376" data-mp="2mkm0lo4fk2bp">
              {tab.label}
              <span className="font-sans text-[0.7rem] tabular-nums tracking-normal text-paper/45" data-id="element-2377" data-mp="fu5tdmdva2bd">
                {counts[tab.view]}
              </span>
            </button>;
      })}

        <button type="button" onClick={() => setOldestFirst(value => !value)} className="label ml-auto inline-flex h-9 items-center border border-paper/25 px-3.5 text-paper/45 transition-colors duration-150 ease-out hover:border-paper/60 hover:text-paper" data-id="element-2378" data-mp="1vdnaqjtftpsq">
          {oldestFirst ? 'Oldest first' : 'Newest first'}
        </button>
      </div>

      {/* Only on Open. On the other views it would be offering to clear rows
          that are not in front of you, which is how people lose things. */}
      {view === 'open' && <ClearBacklog onCleared={load} data-id="element-2379" data-mp="2t0cg93ncmkjc" />}

      {loading && <p className="label mt-12 text-paper/35" data-id="element-2380" data-mp="2o1rh77ju26wh">Loading…</p>}

      {!loading && <div className="mt-4" data-id="element-2381" data-mp="ymuqd9zh1bl5">
          {showNeeds && <DispatchGroup title="Waiting on Portal" rows={groups.needs} busyId={busyId} viewer={viewer} onDone={onDone} onReopen={onReopen} data-id="element-2382" data-mp="yncvyx80x25q" />}
          {showChase && <DispatchGroup title="Waiting on them" rows={groups.chase} busyId={busyId} viewer={viewer} onDone={onDone} onReopen={onReopen} data-id="element-2383" data-mp="132rak58pb20k" />}
          {showDone && <DispatchGroup title="Done" rows={groups.done} busyId={busyId} viewer={viewer} onDone={onDone} onReopen={onReopen} data-id="element-2384" data-mp="1qtd5qvxlwrt5" />}

          {nothing && <p className="label-sm mt-12 text-paper/35" data-id="element-2385" data-mp="1h0xpc31ykbad">
              {showDone ? 'Nothing marked done yet.' : 'Nothing here.'}
            </p>}
        </div>}

      {undo && <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center px-6 pb-24 sm:pb-8" data-id="element-2386" data-mp="29lfr2kl70w9q">
          <div className="flex max-w-full items-center gap-5 border border-ruleDark bg-ink px-5 py-3" data-id="element-2387" data-mp="24qs2klq6w1v5">
            <span className="truncate text-[0.95rem] text-paper/80" data-id="element-2388" data-mp="3nhz8yg4m6cdp">{undo.org} marked done</span>
            <button type="button" onClick={() => void onReopen(undo)} className="label inline-flex h-10 shrink-0 items-center border border-paper/30 px-4 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-2389" data-mp="3sgk80c84qq0k">
              Undo
            </button>
          </div>
        </div>}
    </div>;
}